import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Alert,
    Divider,
    CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { useTheme } from '../../contexts/ThemeContext';
import { cityApi, City } from '../../services/cityApi';
import { createConsignment, CreateConsignmentData } from '../../services/consignmentApi';
import { fetchProfile } from '../../store/slices/profileSlice';
import Toast from '../../components/Toast';
import useToast from '../../hooks/useToast';

interface FormData {
    origin: number | '';
    destination: number | '';
    service_type: 'Overnight' | 'Express' | '';
    // Client fields
    orderId: string;
    cod_amount: string;
    declared_value: string;
    cod_type: 'COD' | 'Prepaid';
    // Consignee
    consigneePhone: string;
    consigneeName: string;
    consigneePhone2: string;
    consigneeAddress: string;
    // Consigner
    consignerName: string;
    consignerPhone: string;
    consignerPhone2: string;
    consignerAddress: string;
    consignerCnic: string;
    // Package
    pieces: string;
    height: string;
    width: string;
    length: string;
    weight: string;
    // Client-only
    special_instructions: string;
    // Non-client fields
    goods_description: string;
    price: string;
    discount: string;
    total_price: string;
}

const ConsignmentCreate: React.FC = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((s: RootState) => s.auth);
    const { user: profileUser } = useSelector((s: RootState) => s.profile);
    const { toast, showSuccess, showError, hideToast } = useToast();

    const [cities, setCities] = useState<City[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const isClient = user?.type === 'CLIENT';

    const [formData, setFormData] = useState<FormData>({
        origin: '',
        destination: '',
        service_type: '',
        orderId: '',
        cod_amount: '',
        declared_value: '',
        cod_type: 'COD',
        consigneePhone: '',
        consigneeName: '',
        consigneePhone2: '',
        consigneeAddress: '',
        consignerName: '',
        consignerPhone: '',
        consignerPhone2: '',
        consignerAddress: '',
        consignerCnic: '',
        pieces: '1',
        height: '1',
        width: '1',
        length: '1',
        weight: '',
        special_instructions: '',
        goods_description: '',
        price: '',
        discount: '0',
        total_price: ''
    });

    // Calculate volumetric weight
    const volumetricWeight = useMemo(() => {
        const h = parseFloat(formData.height) || 0;
        const w = parseFloat(formData.width) || 0;
        const l = parseFloat(formData.length) || 0;
        return (h * w * l) / 5000;
    }, [formData.height, formData.width, formData.length]);

    // Calculate chargeable weight
    const chargeableWeight = useMemo(() => {
        const denseWeight = parseFloat(formData.weight) || 0;
        return Math.max(denseWeight, volumetricWeight);
    }, [formData.weight, volumetricWeight]);

    // Calculate total price for non-client users
    useEffect(() => {
        if (!isClient) {
            const price = parseFloat(formData.price) || 0;
            const discount = parseFloat(formData.discount) || 0;
            const total = price - discount;
            setFormData(prev => ({ ...prev, total_price: total.toString() }));
        }
    }, [formData.price, formData.discount, isClient]);

    // Check if user has a branch assigned
    useEffect(() => {
        if (user && !user.branch_id) {
            setError('You must be assigned to a branch before creating consignments. Please contact the administrator.');
        }
    }, [user]);

    // Fetch client profile and auto-fill consigner info
    useEffect(() => {
        if (isClient && !profileUser) {
            dispatch(fetchProfile());
        }
    }, [isClient, profileUser, dispatch]);

    useEffect(() => {
        if (isClient && profileUser?.clientProfile) {
            const cp = profileUser.clientProfile;
            setFormData(prev => ({
                ...prev,
                consignerName: cp.company_name || prev.consignerName,
                consignerPhone: cp.authorized_phone || prev.consignerPhone,
                consignerAddress: cp.address || prev.consignerAddress,
            }));
        }
    }, [isClient, profileUser]);

    // Load cities
    useEffect(() => {
        const loadCities = async () => {
            setLoading(true);
            try {
                const response = await cityApi.getCities({ per_page: 500, status: '1' });
                setCities(response.cities);

                // Set origin city based on user's branch city (if available)
                if (user?.profile?.city_id) {
                    setFormData(prev => ({ ...prev, origin: user.profile!.city_id! }));
                }
            } catch (err) {
                console.error('Failed to load cities:', err);
            } finally {
                setLoading(false);
            }
        };
        loadCities();
    }, [user]);

    const handleChange = (field: keyof FormData) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }
    ) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));

        // If COD type changes to Prepaid, set cod_amount to 0
        if (field === 'cod_type' && value === 'Prepaid') {
            setFormData(prev => ({ ...prev, cod_amount: '0' }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        // Validate required fields
        if (!formData.origin || !formData.destination || !formData.service_type) {
            setError('Please fill in all required fields');
            return;
        }

        if (!formData.consigneeName || !formData.consigneePhone || !formData.consigneeAddress) {
            setError('Please fill in all consignee information');
            return;
        }

        if (!formData.consignerName || !formData.consignerPhone || !formData.consignerAddress) {
            setError('Please fill in all consigner information');
            return;
        }

        if (!formData.weight) {
            setError('Please enter the package weight');
            return;
        }

        setSubmitting(true);

        try {
            const payload: CreateConsignmentData = {
                origin: Number(formData.origin),
                destination: Number(formData.destination),
                service_type: formData.service_type as 'Overnight' | 'Express',
                pieces: Number(formData.pieces) || 1,
                height: Number(formData.height) || 1,
                width: Number(formData.width) || 1,
                length: Number(formData.length) || 1,
                weight: Number(formData.weight),
                consignerName: formData.consignerName,
                consignerPhone: formData.consignerPhone,
                consignerPhone2: formData.consignerPhone2 || undefined,
                consignerAddress: formData.consignerAddress,
                consigneeName: formData.consigneeName,
                consigneePhone: formData.consigneePhone,
                consigneePhone2: formData.consigneePhone2 || undefined,
                consigneeAddress: formData.consigneeAddress
            };

            if (isClient) {
                payload.cod_type = formData.cod_type;
                payload.orderId = formData.orderId;
                payload.cod_amount = formData.cod_type === 'Prepaid' ? 0 : Number(formData.cod_amount) || 0;
                payload.declared_value = Number(formData.declared_value) || 0;
                payload.special_instructions = formData.special_instructions;
            } else {
                payload.consignerCnic = formData.consignerCnic;
                payload.goods_description = formData.goods_description;
                payload.price = Number(formData.price) || 0;
                payload.discount = Number(formData.discount) || 0;
                payload.total_price = Number(formData.total_price) || 0;
            }

            const response = await createConsignment(payload);
            showSuccess(`Consignment ${response.data.consignment_no} created successfully!`, 'Booking Created');

            // Reset form after successful creation
            setTimeout(() => {
                navigate('/consignments');
            }, 2000);
        } catch (err: any) {
            showError(err.message || 'Failed to create consignment', 'Booking Failed');
        } finally {
            setSubmitting(false);
        }
    };

    if (!user?.branch_id) {
        return (
            <Box>
                <Typography variant="h4" component="h1" gutterBottom>
                    Book Consignment
                </Typography>
                <Alert severity="error" sx={{ mt: 2 }}>
                    You must be assigned to a branch before creating consignments. Please contact the administrator.
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" component="h1" gutterBottom>
                Book Consignment
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <form onSubmit={handleSubmit}>
                <Card sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                            Basic Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <FormControl fullWidth required>
                                    <InputLabel>Origin</InputLabel>
                                    <Select
                                        label="Origin"
                                        value={formData.origin}
                                        onChange={handleChange('origin') as any}
                                        disabled={loading}
                                    >
                                        {cities.map(city => (
                                            <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <FormControl fullWidth required>
                                    <InputLabel>Service Type</InputLabel>
                                    <Select
                                        label="Service Type"
                                        value={formData.service_type}
                                        onChange={handleChange('service_type') as any}
                                    >
                                        <MenuItem value="Overnight">Overnight</MenuItem>
                                        <MenuItem value="Express">Express</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <FormControl fullWidth required>
                                    <InputLabel>Destination</InputLabel>
                                    <Select
                                        label="Destination"
                                        value={formData.destination}
                                        onChange={handleChange('destination') as any}
                                        disabled={loading}
                                    >
                                        {cities.map(city => (
                                            <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {isClient ? (
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField
                                        fullWidth
                                        label="Order ID"
                                        value={formData.orderId}
                                        onChange={handleChange('orderId')}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="COD Amount"
                                        type="number"
                                        value={formData.cod_amount}
                                        onChange={handleChange('cod_amount')}
                                        disabled={formData.cod_type === 'Prepaid'}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Declared Value"
                                        type="number"
                                        value={formData.declared_value}
                                        onChange={handleChange('declared_value')}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Order Type</InputLabel>
                                        <Select
                                            label="Order Type"
                                            value={formData.cod_type}
                                            onChange={handleChange('cod_type') as any}
                                        >
                                            <MenuItem value="COD">COD</MenuItem>
                                            <MenuItem value="Prepaid">Prepaid</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        ) : (
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        label="Goods Description"
                                        multiline
                                        rows={2}
                                        value={formData.goods_description}
                                        onChange={handleChange('goods_description')}
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </CardContent>
                </Card>

                <Card sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                            Consignee Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Phone Number"
                                    value={formData.consigneePhone}
                                    onChange={handleChange('consigneePhone')}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Name"
                                    value={formData.consigneeName}
                                    onChange={handleChange('consigneeName')}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Second Phone Number"
                                    value={formData.consigneePhone2}
                                    onChange={handleChange('consigneePhone2')}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Address"
                                    multiline
                                    rows={2}
                                    value={formData.consigneeAddress}
                                    onChange={handleChange('consigneeAddress')}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Card sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                            Consigner Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Name"
                                    value={formData.consignerName}
                                    onChange={handleChange('consignerName')}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Phone Number"
                                    value={formData.consignerPhone}
                                    onChange={handleChange('consignerPhone')}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Second Phone Number"
                                    value={formData.consignerPhone2}
                                    onChange={handleChange('consignerPhone2')}
                                />
                            </Grid>
                            {!isClient && (
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="CNIC"
                                        value={formData.consignerCnic}
                                        onChange={handleChange('consignerCnic')}
                                    />
                                </Grid>
                            )}
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Address"
                                    multiline
                                    rows={2}
                                    value={formData.consignerAddress}
                                    onChange={handleChange('consignerAddress')}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Card sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                            Package Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 3 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Pieces"
                                    type="number"
                                    value={formData.pieces}
                                    onChange={handleChange('pieces')}
                                    inputProps={{ min: 1 }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Height (CM)"
                                    type="number"
                                    value={formData.height}
                                    onChange={handleChange('height')}
                                    inputProps={{ min: 1, max: 150 }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Width (CM)"
                                    type="number"
                                    value={formData.width}
                                    onChange={handleChange('width')}
                                    inputProps={{ min: 1, max: 150 }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Length (CM)"
                                    type="number"
                                    value={formData.length}
                                    onChange={handleChange('length')}
                                    inputProps={{ min: 1, max: 150 }}
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Dense Weight (KG)"
                                    type="number"
                                    value={formData.weight}
                                    onChange={handleChange('weight')}
                                    inputProps={{ min: 0, max: 15, step: 0.1 }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Volumetric Weight (KG)"
                                    value={volumetricWeight.toFixed(2)}
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Chargeable Weight (KG)"
                                    value={chargeableWeight.toFixed(2)}
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                        </Grid>

                        {!isClient ? (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Chargeable Amount"
                                            type="number"
                                            value={formData.price}
                                            onChange={handleChange('price')}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <TextField
                                            fullWidth
                                            label="Discount"
                                            type="number"
                                            value={formData.discount}
                                            onChange={handleChange('discount')}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <TextField
                                            fullWidth
                                            label="Total Chargeable Amount"
                                            value={formData.total_price}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12 }}>
                                        <TextField
                                            fullWidth
                                            label="Special Instructions"
                                            multiline
                                            rows={3}
                                            value={formData.special_instructions}
                                            onChange={handleChange('special_instructions')}
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </CardContent>
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/consignments')}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={submitting || !user?.branch_id}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Submit'}
                    </Button>
                </Box>
            </form>
            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default ConsignmentCreate;
